<template>
  <div
    class="h-full flex"
    @mouseover="isHoveredOver = true"
    @mouseleave="isHoveredOver = false"
  >
    <div class="w-full relative bg-white">
      <div
        ref="editorContainer"
        class="h-full w-full flex items-start"
        @click="showLockWarning"
      >
        <FloatingMenu
          v-if="isSynthesisAiSuggested"
          placement="bottom"
          :offset="[0, 8]"
          class="h-full"
        >
          <template #activator="{ show }">
            <button
              class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-800 rounded-md p-2"
              :class="{
                'border border-green-500': isSynthesisAiSuggested,
              }"
              @click="show"
            >
              <CheckIcon class="w-4" />
              <SparklesIcon class="w-4" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col bg-white rounded-lg shadow-lg py-2">
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/accept-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="acceptAiSuggestion(hide)"
              >
                <CheckIcon
                  class="size-5 group-hover/accept-suggestion:text-green-600"
                />
                Accept AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/edit-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="editAiSuggestion(hide)"
              >
                <PencilIcon
                  class="size-4 mr-1 group-hover/edit-suggestion:text-blue-600"
                />
                Edit AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/reject-suggestion font-medium text-gray-900 flex items-center gap-2"
                @click="rejectAiSuggestion(hide)"
              >
                <XMarkIcon
                  class="size-5 group-hover/reject-suggestion:text-red-600"
                />
                Reject AI suggestion
              </button>
            </div>
          </template>
        </FloatingMenu>
        <button
          v-else
          class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-2 rounded-md text-gray-800 p-2"
          :class="{
            'bg-green-500 text-white': attributeStructure?.isSynthesisFinalized,
          }"
          @click="toggleSynthesisFinalization"
        >
          <CheckIcon class="w-4" />
        </button>
        <div class="flex flex-col w-full h-full">
          <Editor
            v-if="isVisible"
            v-model:html="synthesis"
            :is-expanded="false"
            :provider="review.dataExtractionProvider"
            :user-name="userName"
            :user-color="color"
            :disabled="
              review.isReviewReadonly.value ||
              attributeStructure?.isSynthesisFinalized ||
              isSynthesisAiSuggested ||
              isGeneratingAnswer
            "
            :field="`review-${review.entity.value.id}-synthesis-${attributeStructure?.id}`"
            :placeholder="`Enter the synthesis of ${attributeStructure?.label}`"
            class="h-full flex min-h-0"
            editor-classes=" p-3 hover:overflow-auto overflow-hidden pb-10 rounded-b-lg min-h-100 h-full w-full"
          />
        </div>
      </div>
      <div
        v-if="!review.isReviewReadonly.value"
        class="absolute top-10 -right-4 items-center"
      >
        <div v-if="!attributeStructure?.isSynthesisFinalized">
          <Transition name="fade">
            <div
              v-show="
                (!review.isReviewReadonly.value && isHoveredOver) ||
                isGeneratingAnswer
              "
            >
              <button
                v-tooltip.top="
                  !isAssistantConfigured
                    ? 'The AI model is not configured. Please click to configure it in settings.'
                    : null
                "
                :disabled="isLoading"
                class="rounded-lg w-8 h-8 flex items-center justify-center bg-purple-600 text-white"
                @click="handleClick"
              >
                <SparklesIcon
                  class="w-5 h-5"
                  :class="{
                    'fill-white': !isGeneratingAnswer,
                    'animate-fill-gradient': isGeneratingAnswer,
                  }"
                />
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from '@app/utils/injectStrict'
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { AuthKey, EvidenceServiceApiKey } from '@app/injectionKeys'
import {
  PencilIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/vue/24/solid'
import useAiAssistant from '@app/composables/useAiAssistant'
import { useRouter } from 'vue-router'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { Role } from '@core/domain/types/role.type'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import { AttributeStructure } from '@core/domain/models/data-extraction-plan-attribute'
import { ReviewKey } from '@app/views/Review/use-review'
import Editor from '../components/Editor.vue'
import { HttpException } from '@core/exceptions/http.exception'
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'
import { errorMessage } from '@app/utils/error-message'

const evidenceServiceApi = injectStrict(EvidenceServiceApiKey)
const { isConfigured: isAssistantConfigured } = useAiAssistant()
const router = useRouter()

const props = defineProps<{
  attributeStructure: AttributeStructure
}>()
const synthesis = ref('')
const review = injectStrict(ReviewKey)
const { user, color } = injectStrict(AuthKey)
const isHoveredOver = ref(false)
const snackbar = useSnackbar()

const isSynthesisAiSuggested = computed(
  () => props.attributeStructure?.isSynthesisAiSuggested ?? false,
)

function showLockWarning() {
  if (!review.isReviewReadonly.value) return
  const isAdmin = user.value?.role === Role.ADMIN
  let message = ''
  if (
    review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED &&
    !review.isLocked.value
  ) {
    message = isAdmin
      ? 'The plan is unlocked, lock it to make changes.'
      : 'The plan is unlocked. Please contact an admin to make changes.'
  } else if (review.isLocked.value) {
    message = isAdmin
      ? 'The review is locked, unlock it to make changes.'
      : 'The review is locked. Please contact an admin to make changes.'
  }
  if (message) {
    snackbar.show(SnackbarState.ERROR, message)
  }
}

const userName = computed(
  () => user.value?.firstName + ' ' + user.value?.lastName,
)

const isGeneratingAnswer = ref(false)
const { isLoading, start: startLoading, stop: stopLoading } = useLoading()

async function synthesizeAttribute(attributeId: string) {
  isGeneratingAnswer.value = true
  startLoading()
  try {
    const { data } =
      await evidenceServiceApi.api.reviewsControllerSynthesizeDataExtractionAttribute(
        review.entity.value.id,
        {
          reviewId: review.entity.value.id,
          attributeId: attributeId,
        },
      )
    synthesis.value = data
    await review.refresh()
  } catch (e: any) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(SnackbarState.ERROR, 'An error occured, please try again.')
    }
    throw e
  } finally {
    isGeneratingAnswer.value = false
    stopLoading()
  }
}

async function acceptAiSuggestion(hide: () => void) {
  startLoading()
  try {
    await finalizeSynthesis()
    hide()
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    stopLoading()
  }
}

async function rejectAiSuggestion(hide: () => void) {
  startLoading()
  try {
    await review.clearSynthesisAiSuggestion(props.attributeStructure.id)
    synthesis.value = ''
    hide()
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    stopLoading()
  }
}

async function editAiSuggestion(hide: () => void) {
  startLoading()
  try {
    await review.editSynthesisAiSuggestion(props.attributeStructure.id)
    hide()
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    stopLoading()
  }
}

async function toggleSynthesisFinalization() {
  startLoading()
  try {
    if (props.attributeStructure.isSynthesisFinalized) {
      await unfinalizeSynthesis()
    } else {
      await finalizeSynthesis()
    }
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    stopLoading()
  }
}

async function finalizeSynthesis() {
  await review.finalizeAttributeSynthesis(props.attributeStructure.id)
  await review.refresh()
  snackbar.success('Success')
}

async function unfinalizeSynthesis() {
  await review.unfinalizeAttributeSynthesis(props.attributeStructure.id)
  await review.refresh()
  snackbar.success('Success')
}

const isVisible = ref(false)
const editorContainer = useTemplateRef('editorContainer')

function handleClick() {
  if (isAssistantConfigured.value) {
    synthesizeAttribute(props.attributeStructure.id)
  } else {
    router.push('/settings')
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true
        observer.disconnect()
      }
    },
    {
      rootMargin: '50px',
      threshold: 0.1,
    },
  )

  if (editorContainer.value) {
    observer.observe(editorContainer.value)
  }

  onUnmounted(() => {
    observer.disconnect()
  })
})
</script>
